import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Money, HideValuesToggle } from "@/components/HideValues";
import { PeriodPicker, PERIOD_RANGES } from "@/components/PeriodPicker";
import { Button } from "@/components/ui/button";
import { selectCls } from "@/components/erp/ErpUi";
import { toast } from "sonner";
import { Car, Download, FileText, Loader2 } from "lucide-react";

const download = async (path, params, filename) => {
  try {
    const { data } = await api.get(path, { params, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) { toast.error(formatApiError(e)); }
};

export default function OwnerVehiclesByEmployee() {
  const [preset, setPreset] = useState("month");
  const [[from, to], setRange] = useState(() => PERIOD_RANGES[2].range());
  const [role, setRole] = useState("mecanico");
  const [name, setName] = useState("");
  const [d, setD] = useState(null);
  const params = { from, to, role, ...(name ? { name } : {}) };
  useEffect(() => {
    setD(null);
    api.get("/reports/vehicles-by-employee", { params }).then(({ data }) => setD(data)).catch((e) => { toast.error(formatApiError(e)); setD({ rows: [], employees: [], totals: {} }); });
  }, [from, to, role, name]); // eslint-disable-line react-hooks/exhaustive-deps
  const th = "px-3 py-2 text-left text-[11px] font-mono-caps uppercase text-zinc-500";
  return (
    <div className="space-y-5 animate-fade-up" data-testid="vehicles-by-employee">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500 uppercase"><Car className="h-4 w-4" /> Relatórios</div>
          <h1 className="font-display text-3xl font-black uppercase text-zinc-100 mt-1">Veículos por Funcionário</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <HideValuesToggle />
          <Button data-testid="vbe-export-pdf" variant="outline" onClick={() => download("/reports/vehicles-by-employee/pdf", params, "veiculos-por-funcionario.pdf")} className="bg-zinc-900 border-zinc-800"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
          <Button data-testid="vbe-export-xlsx" variant="outline" onClick={() => download("/reports/vehicles-by-employee.xlsx", params, "veiculos-por-funcionario.xlsx")} className="bg-zinc-900 border-zinc-800"><Download className="h-4 w-4 mr-1" /> Planilha</Button>
        </div>
      </div>
      <PeriodPicker preset={preset} setPreset={setPreset} from={from} to={to} setRange={setRange} testid="vbe-period" />
      <div className="flex flex-wrap gap-3">
        <select data-testid="vbe-role" value={role} onChange={(e) => { setRole(e.target.value); setName(""); }} className={`${selectCls} !w-auto`}>
          <option value="mecanico">Mecânico</option><option value="consultor">Consultor</option>
        </select>
        <select data-testid="vbe-employee" value={name} onChange={(e) => setName(e.target.value)} className={`${selectCls} !w-auto`}>
          <option value="">Todos os funcionários</option>
          {(d?.employees || []).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div className="rounded-2xl border border-card2-border bg-card2 overflow-x-auto">
        {!d ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-green-brand" /></div> : (
          <table className="w-full text-sm" data-testid="vbe-table">
            <thead><tr className="border-b border-zinc-800">
              {["Funcionário", "OS", "Cliente", "Veículo", "Placa", "Peças", "Serviços", "Etapa"].map((h) => <th key={h} className={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.id} data-testid={`vbe-row-${r.id}`} className="border-b border-zinc-900">
                  <td className="px-3 py-2 text-zinc-200">{r.employee}</td>
                  <td className="px-3 py-2"><Link to={`/owner/ordens/${r.id}`} className="text-green-300 hover:underline">{r.order_number != null ? `#${r.order_number}` : "—"}</Link></td>
                  <td className="px-3 py-2 text-zinc-300">{r.customer}</td>
                  <td className="px-3 py-2 text-zinc-300">{r.vehicle}</td>
                  <td className="px-3 py-2 font-mono text-zinc-200">{r.plate}</td>
                  <td className="px-3 py-2 tabular-nums"><Money value={r.parts} /></td>
                  <td className="px-3 py-2 tabular-nums"><Money value={r.labor} /></td>
                  <td className="px-3 py-2 text-zinc-300">{r.status_label}</td>
                </tr>
              ))}
              {!d.rows.length && <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500" data-testid="vbe-empty">Nenhuma OS no período.</td></tr>}
            </tbody>
            {!!d.rows.length && (
              <tfoot><tr className="font-bold text-zinc-100" data-testid="vbe-totals">
                <td className="px-3 py-2">Total</td><td className="px-3 py-2">{d.totals.orders} OS</td><td colSpan={3} />
                <td className="px-3 py-2 tabular-nums"><Money value={d.totals.parts} /></td><td className="px-3 py-2 tabular-nums"><Money value={d.totals.labor} /></td><td />
              </tr></tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
}
